/**
 * ChatSuggestions Component
 * Displays AI-powered autocomplete suggestions in a popover above the chat input.
 * Shows automatically as user types (debounced).
 *
 * - Pro suggestions have muted text and Pro icon badge
 * - Clicking a suggestion submits the message
 */

import { TierProFilled } from '@heidi/design-system/icons';
import { useChatSuggestions } from '@/components/chat/hooks/useChatSuggestions';
import type { ChatSuggestion } from '@/components/chat/types/types';
import { Button } from '@heidi/design-system/button';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@heidi/design-system/popover';
import { Skeleton } from '@heidi/design-system/skeleton';
import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import type { Editor } from '@tiptap/react';

const SKELETON_COUNT = 4;

interface ChatSuggestionsProps {
  editor: Editor | null;
  disabled?: boolean;
  onSend: (message: string) => void;
}

interface SuggestionItemProps {
  suggestion: ChatSuggestion;
  onClick: () => void;
}

const SuggestionItem = ({ suggestion, onClick }: SuggestionItemProps) => {
  const isPro = suggestion.type === 'pro';

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className={cn(
        'h-auto w-full justify-start gap-2 whitespace-normal px-3 py-2 text-left',
        isPro ? 'text-tier-pro-text' : 'text-text-secondary'
      )}
    >
      <TypographyCaption className="min-w-0 flex-1">{suggestion.content}</TypographyCaption>
      {isPro && (
        <span className="flex shrink-0 items-center gap-1 text-tier-pro-text">
          <TierProFilled className="size-2.5 text-tier-pro" />
          <TypographyCaption>Pro</TypographyCaption>
        </span>
      )}
    </Button>
  );
};

const SuggestionsSkeleton = () => (
  <div className="flex flex-col gap-3 p-2">
    {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
      <Skeleton key={i} className="h-5 w-full rounded-xs bg-background-tertiary" />
    ))}
  </div>
);

export const ChatSuggestions = ({
  editor,
  disabled = false,
  onSend,
}: ChatSuggestionsProps) => {
  const {
    suggestions,
    handleSuggestionClick,
    isSuggestionsPending,
    shouldShowSuggestions,
  } = useChatSuggestions({ editor, enabled: !disabled, onSend });

  const isOpen = shouldShowSuggestions || isSuggestionsPending;

  return (
    <Popover open={isOpen}>
      <PopoverAnchor asChild>
        <div className="h-0 w-full" />
      </PopoverAnchor>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-[var(--radix-popover-trigger-width)] p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {isSuggestionsPending && suggestions.length === 0 ? (
          <SuggestionsSkeleton />
        ) : (
          <div className="flex flex-col">
            {suggestions.map((suggestion, index) => (
              <SuggestionItem
                key={`${suggestion.content.slice(0, 20)}-${index}`}
                suggestion={suggestion}
                onClick={() => handleSuggestionClick(index)}
              />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
